import React, { useState, useRef, useEffect } from 'react';
import { useSvgStore } from '../../store/svgStore';
import svgAPIService from '../../services/svgApi';
import SvgPreview from '../common/SvgPreview';
import SvgAssetLibrary from '../library/SvgAssetLibrary';
import { mockGenerateSvgByPrompt } from '../../services/mockAiSvg';

type LeftPanelTab = 'ai' | 'library';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  svgContent?: string;
  createdAt: number;
}

const AISvgChatPanel: React.FC = () => {
  const [prompt, setPrompt] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<LeftPanelTab>('ai');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [fullscreenSvg, setFullscreenSvg] = useState<string | null>(null);

  // 新增：获取 canvas 信息以便计算中心点
  const canvas = useSvgStore((state) => state.canvas);
  const addAiSvgToCanvas = useSvgStore((state) => state.addAiSvgToCanvas);
  const addAsset = useSvgStore((state) => state.addAsset);
  const addAiHistory = useSvgStore((state) => state.addAiHistory);

  // 新增：用于自动滚动的引用
  const chatScrollRef = useRef<HTMLDivElement | null>(null);

  const appendMessage = (message: ChatMessage): void => {
    setMessages((prev) => [...prev, message]);
  };

  const handleGenerate = async (): Promise<void> => {
    const trimmed = prompt.trim();
    if (!trimmed) {
      return;
    }

    const now = Date.now();

    const userMessage: ChatMessage = {
      id: `user_${now}`,
      role: 'user',
      text: trimmed,
      createdAt: now,
    };
    appendMessage(userMessage);

    setIsLoading(true);
    try {
      const useMock = false;
      const result = useMock
        ? await mockGenerateSvgByPrompt(trimmed)
        : await svgAPIService.generateSvgByPrompt({ prompt: trimmed });

      const aiMessage: ChatMessage = {
        id: `ai_${Date.now()}`,
        role: 'assistant',
        text:
          '这是根据你当前描述生成的界面预览。如果不满意，可以继续补充说明，我会帮你逐步优化布局和样式。',
        svgContent: result.svg,
        createdAt: Date.now(),
      };
      setPrompt('');
      appendMessage(aiMessage);

      await addAiHistory({
        prompt: trimmed,
        generated_svg: result.svg,
        model: result.model,
        used_in_canvas: false,
        saved_to_library: false,
      });

      
    } catch (error) {
      console.error('生成 SVG 出错: AISvgChatPanel.handleGenerate', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddToCanvas = (svg: string | undefined): void => {
    if (!svg) {
      return;
    }
    
    // 修复：确保 canvas 存在，并提供更安全的默认值
    const canvasWidth = canvas?.width || 800; // 假设默认宽
    const canvasHeight = canvas?.height || 600; // 假设默认高
    
    // 调整：减少 offset 假设，更接近绝对中心
    // 如果之前"偏右"，说明 X 太大。
    // 这里我们假设元素约 200px 宽，偏移 100px
    const centerX = (canvasWidth / 2);
    const centerY = (canvasHeight / 2);

    // 传入坐标参数 (需要 svgStore 支持接收第三个参数作为属性覆盖)
    // @ts-ignore: 假设 addAiSvgToCanvas 已经扩展了支持 extraAttributes
    addAiSvgToCanvas(svg, 'AI 生成图层', { x: centerX, y: centerY });
  };

  const handleSaveToLibrary = async (
    svg: string | undefined,
    promptText: string,
  ): Promise<void> => {
    if (!svg) {
      return;
    }
    try {
      await addAsset({
        name: `AI_${Date.now()}`,
        svg_code: svg,
        from_ai_prompt: promptText,
        is_public: false,
      });
    } catch (error) {
      console.error('保存到素材库失败: AISvgChatPanel.handleSaveToLibrary', error);
    }
  };

  const handleExportSvg = (svg: string | undefined): void => {
    if (!svg) {
      return;
    }
    const blob = new Blob([svg], {
      type: 'image/svg+xml;charset=utf-8',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'ai-generated.svg';
    link.click();
    URL.revokeObjectURL(url);
  };

  const openFullscreenPreview = (svg: string | undefined): void => {
    if (!svg) {
      return;
    }
    setFullscreenSvg(svg);
  };

  const closeFullscreenPreview = (): void => {
    setFullscreenSvg(null);
  };

  const renderMessageBubble = (
    message: ChatMessage,
    index: number,
  ): JSX.Element => {
    if (message.role === 'user') {
      return (
        <div key={message.id} className="ai-svg-chat-row-user">
          <div className="ai-svg-user-bubble">
            <div className="ai-svg-user-text">{message.text}</div>
          </div>
        </div>
      );
    }

    const linkedPrompt =
      messages[index - 1]?.role === 'user' ? messages[index - 1]?.text ?? '' : '';

    return (
      <div key={message.id} className="ai-svg-chat-row-ai">
        <div className="ai-svg-ai-avatar" />
        {/* <div style={{width: '30px', height: '30px'}}>
            <img src="/static/media/ai.png" alt="AI Assistant" style={{width: '100%', height: '100%'}} />
        </div> */}
        <div className="ai-svg-ai-bubble-body">
          <div className="ai-svg-ai-bubble-title">AI 分析结果</div>
          <div className="ai-svg-ai-bubble-desc">{message.text}</div>

          {message.svgContent && (
            <>
              <div className="ai-svg-ai-inline-preview">
                <div className="ai-svg-ai-inline-preview-inner">
                  <SvgPreview svgContent={message.svgContent} />
                </div>
              </div>

              <div className="ai-svg-ai-inline-actions">
                <button
                  type="button"
                  className="ai-svg-secondary-button-light"
                  onClick={() => handleAddToCanvas(message.svgContent)}
                >
                  ➕ 添加到画布
                </button>
                <button
                  type="button"
                  className="ai-svg-secondary-button-light"
                  onClick={() =>
                    handleSaveToLibrary(message.svgContent, linkedPrompt)
                  }
                >
                  📦 保存到素材库
                </button>
                <button
                  type="button"
                  className="ai-svg-secondary-button-light"
                  onClick={() => handleExportSvg(message.svgContent)}
                >
                  ⬇️ 导出 SVG
                </button>
                <button
                  type="button"
                  className="ai-svg-ghost-button-light"
                  onClick={() => openFullscreenPreview(message.svgContent)}
                >
                  放大查看
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  const renderAiTabContent = (): JSX.Element => {
    return (
      <>
        <div className="ai-svg-chat-scroll"  ref={chatScrollRef}>
          {messages.length === 0 && (
            <div className="ai-svg-ai-bubble">
              <div className="ai-svg-ai-avatar" />
              <div className="ai-svg-ai-bubble-body">
                <div className="ai-svg-ai-bubble-title">
                  描述你想要生成的界面或图形
                </div>
                <div className="ai-svg-ai-bubble-desc">
                  例如：浅色渐变背景下，一张展示下载量的文件卡片，上方有「查看跳出率详情」按钮，下方是一条对话提示。
                </div>
              </div>
            </div>
          )}

          {messages.map((message, index) =>
            renderMessageBubble(message, index),
          )}

          {isLoading && (
            <div className="ai-svg-chat-row-ai">
              <div className="ai-svg-ai-avatar" />
              <div className="ai-svg-ai-bubble-body">
                <div className="ai-svg-ai-bubble-desc">
                  ⏳ 正在分析数据并生成 SVG，请稍候…
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="ai-svg-input-area">
          {/* <div className="ai-svg-input-hints">
            <span className="ai-svg-input-chip">生成数据分析界面</span>
            <span className="ai-svg-input-chip">识别可执行操作和按钮</span>
          </div> */}

          <div className="ai-svg-input-footer">
            <textarea
              className="ai-svg-prompt-input-light"
            //   placeholder="用自然语言描述你想要的图形或界面，例如：生成一个带文件卡片和导出按钮的分析结果气泡…"
              value={prompt}
              onChange={(event) => setPrompt(event.target.value)}
              rows={1}
            />

            <button
              type="button"
              className="ai-svg-primary-button-light"
              onClick={handleGenerate}
              disabled={isLoading || !prompt.trim()}
              aria-label="发送"
            >
              ➜
            </button>
          </div>
        </div>
      </>
    );
  };

  const renderLibraryTabContent = (): JSX.Element => {
    return (
      <div className="ai-svg-left-library-wrapper">
        <SvgAssetLibrary />
      </div>
    );
  };

  // 新增：当消息或 loading 状态变化时，自动滚动到底部
  useEffect(() => {
    const container = chatScrollRef.current;
    if (!container) {
      return;
    }
    // 使用 requestAnimationFrame，确保 DOM 更新完成后再滚动
    window.requestAnimationFrame(() => {
      try {
        container.scrollTop = container.scrollHeight;
      } catch (error) {
        console.error('AI 聊天自动滚动失败: AISvgChatPanel.useEffect', error);
      }
    });
  }, [messages, isLoading]);

  return (
    <div className="ai-svg-panel-left-light">
      <div className="ai-svg-left-header">
        <div className="ai-svg-left-title-group">
          <div className="ai-svg-left-title">AI SVG 数据助手</div>
          <div className="ai-svg-left-subtitle">
            通过对话生成、预览并管理你的 SVG 数据界面。
          </div>
        </div>
        <div className="ai-svg-left-tabs">
          <button
            type="button"
            className={
              activeTab === 'ai'
                ? 'ai-svg-left-tab ai-svg-left-tab-active'
                : 'ai-svg-left-tab'
            }
            onClick={() => setActiveTab('ai')}
          >
            AI 对话
          </button>
          <button
            type="button"
            className={
              activeTab === 'library'
                ? 'ai-svg-left-tab ai-svg-left-tab-active'
                : 'ai-svg-left-tab'
            }
            onClick={() => setActiveTab('library')}
          >
            素材库
          </button>
        </div>
      </div>

      <div className="ai-svg-left-body">
        {activeTab === 'ai' ? renderAiTabContent() : renderLibraryTabContent()}
      </div>

      {fullscreenSvg && (
        <div className="ai-svg-fullscreen-backdrop" onClick={closeFullscreenPreview}>
          <div
            className="ai-svg-fullscreen-modal"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="ai-svg-fullscreen-header">
              <span className="ai-svg-fullscreen-title">SVG 放大预览</span>
              <button
                type="button"
                className="ai-svg-ghost-button-light"
                onClick={closeFullscreenPreview}
              >
                关闭
              </button>
            </div>
            <div className="ai-svg-fullscreen-content">
              <SvgPreview svgContent={fullscreenSvg} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AISvgChatPanel;